import React, { useState, useEffect } from "react";
import KnowtexEMSuggestion from "./KnowtexEMSuggestion";
import { calculateLevels, calculateTimeBasedLevel } from "./emlevelsfcn";
import sendServerLogging from "../../../utils/sendServerLogging";
import userJSON from "../../../utils/userJSON";
import { toast } from "react-toastify";

export default function EMSection({
  isRationaleProvided,
  setIsRationaleProvided,
  mdm_selected,
  setMDMSelected,
  mdm_suggested,
  userRationale,
  setUserRationale,
  showRationale,
  setShowRationale,
  setFinalSelectedLevel,
  patient,
  setRecommendedLevel,
  setFinalSelectedTs,
  setMDMSelectedTs,
  note,
  encounterTime,
  setEncounterTime,
  status,
  emData,
  setEMData,
  setNote,
  org,
  setTimeSpentChanged,
  onSaveNote,
  hideEMCodes = false,
}) {
  const [activeButton, setActiveButton] = useState(null);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [levels, setLevels] = useState({});
  const [timeBasedLevel, setTimeBasedLevel] = useState(null);
  const [useTimeBasedCoding, setUseTimeBasedCoding] = useState(false);

  // Map patient type to visit type display
  const getVisitTypeFromPatientType = (patientType) => {
    switch (patientType) {
      case "new":
        return "New Patient";
      case "fu":
        return "Follow Up";
      case "telemed":
        return "Telemedicine";
      default:
        return "New Patient";
    }
  };

  useEffect(() => {
    const lvls = calculateLevels(emData.mdm);
    setLevels(lvls);

    // Calculate time-based level
    const timeLevel = calculateTimeBasedLevel(
      emData?.time,
      emData?.patient_type
    );
    setTimeBasedLevel(timeLevel);

    // Determine if we should use time-based coding
    // Use time-based if time level is greater than MDM level
    const shouldUseTimeBased = timeLevel && lvls.note && timeLevel > lvls.note;
    setUseTimeBasedCoding(shouldUseTimeBased);

    // Store Knowtex's recommended numeric level in the parent:
    // Use time-based level if it's higher, otherwise use MDM level
    const recommendedLevel = shouldUseTimeBased ? timeLevel : lvls.note;
    if (recommendedLevel) {
      setRecommendedLevel(recommendedLevel);
    }

    // Pre-populate encounter time from emData if available and not already set
    if (emData?.time) {
      setEncounterTime(emData.time);
    }
  }, [emData, setRecommendedLevel, encounterTime, setEncounterTime]);

  // This is the FIRST row of level buttons
  const handleClick = (selectedLevel) => {
    // Enhanced server logging with emData
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "E&M_SELECTED_CODE_CLICKED",
        eventData: {
          MRN: patient.mrn,
          EM_Level: selectedLevel,
          Recommended_Code: emData?.e_and_m_code,
          Selection_Basis: emData?.selection_on,
        },
      },
    });

    const timestampInSeconds = Math.floor(Date.now() / 1000);

    setActiveButton(selectedLevel);
    setMDMSelected(selectedLevel);
    setMDMSelectedTs(timestampInSeconds);

    // If user picks the same as recommended => skip second row, set final
    if (parseInt(selectedLevel) === parseInt(levels.note)) {
      setShowRecommendations(false);
      setShowRationale(false);
      setIsRationaleProvided(true);
      setFinalSelectedLevel(selectedLevel);
      setFinalSelectedTs(timestampInSeconds);
    } else {
      // If user picks a different level => reveal second row
      setShowRecommendations(true);
      setShowRationale(false);
      setIsRationaleProvided(false);
      //setFinalSelectedLevel(null);
      setFinalSelectedTs(null);
    }
  };

  const getVisitType = () => {
    if (emData?.patient_type) {
      return getVisitTypeFromPatientType(emData.patient_type);
    }
    return "New Patient";
  };

  //server logging
  const sendTimeSpentLog = (mrn, patientId, timeSpent) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "ADD_MEDICATION_ORDER",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          time_spent: timeSpent,
        },
      },
    });
  };

  return (
    <div className="flex flex-col">
      {/* Header row: E&M Codes title (hidden for Psychology) + Time Spent (always shown) */}
      <div className={`flex flex-row ${!hideEMCodes ? "justify-between" : ""} items-start mb-4`}>
        {!hideEMCodes && (
          <div className="flex flex-col flex-1">
            <h2 className="text-accent1 text-xl font-medium mb-2">E&M Codes</h2>
            {org !== "VA" && (
              <h3 className="text-light-grey text-md font-medium mb-2">
                Visit Type: {getVisitType()}
              </h3>
            )}
          </div>
        )}

        {/* Time Spent — always visible regardless of specialty */}
        <div className="flex flex-col min-w-[300px]">
          <div className="mb-4">
            <label
              htmlFor="encounter-time-minutes"
              className="block text-accent1 text-sm font-medium mb-2"
            >
              Total Time Spent (minutes)
            </label>
            <div className="flex flex-row gap-4 items-center">
              <input
                id="encounter-time-minutes"
                type="number"
                aria-label="Total Time Spent in minutes"
                readOnly={status === "Locked"}
                className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-green focus:border-transparent bg-secondary-grey text-black"
                defaultValue={
                  encounterTime === "" ||
                    encounterTime === null ||
                    encounterTime === undefined
                    ? emData?.time === null || emData?.time === undefined
                      ? ""
                      : Math.floor(emData.time)
                    : Math.floor(parseFloat(encounterTime) || 0)
                }
                onChange={(e) => {
                  const newTime = e.target.value;
                  const timeValue = newTime === "" ? 0 : parseFloat(newTime) || 0;

                  sendTimeSpentLog(patient.mrn, patient.Patient_Id, timeValue);

                  // Calculate new time-based level
                  const newTimeLevel = calculateTimeBasedLevel(
                    timeValue,
                    emData?.patient_type
                  );
                  setTimeBasedLevel(newTimeLevel);

                  // Determine if we should use time-based coding
                  const shouldUseTimeBased =
                    newTimeLevel && levels.note && newTimeLevel > levels.note;
                  setUseTimeBasedCoding(shouldUseTimeBased);

                  // Track if time change affects selection basis
                  if (
                    setTimeSpentChanged &&
                    shouldUseTimeBased !== useTimeBasedCoding
                  ) {
                    setTimeSpentChanged(true);
                  }

                  // Update recommended level if time-based is higher
                  const newRecommendedLevel = shouldUseTimeBased
                    ? newTimeLevel
                    : levels.note;
                  if (newRecommendedLevel && setRecommendedLevel) {
                    setRecommendedLevel(newRecommendedLevel);
                  }

                  // Update emData.time directly
                  if (setEMData && emData) {
                    setEMData({
                      ...emData,
                      time: timeValue,
                    });
                  }
                  // Also update encounterTime for backward compatibility
                  if (setEncounterTime) {
                    setEncounterTime(newTime);
                  }
                }}
                placeholder="Minutes"
                min="0"
                step="1"
              />
              <button
                type="button"
                onClick={() => {
                  if (setNote && emData?.time) {
                    const timeMinutes = Math.floor(emData.time);
                    const timeStatement = `Total minutes of encounter: ${timeMinutes}\nThis time includes time spent with the patient as well as time spent documenting in the medical record, reviewing patient's records and tests, obtaining history, placing orders, communicating with other healthcare professionals, counseling the patient, family, or caregiver, and/or care coordination for the diagnoses above.`;

                    const existingNote = note || "";
                    const timeLineRegex = /Total minutes of encounter:\s*\d+/;
                    const updatedNote = timeLineRegex.test(existingNote)
                      ? existingNote.replace(
                        timeLineRegex,
                        `Total minutes of encounter: ${timeMinutes}`
                      )
                      : `${existingNote}${existingNote.trim() ? "\n\n" : ""}${timeStatement}`;

                    setNote(updatedNote);
                    if (onSaveNote) {
                      onSaveNote(updatedNote);
                      toast.success("Time statement inserted into note");
                    }
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Insert into note
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* E&M level selection — hidden for Psychology specialty */}
      {!hideEMCodes && (
        <div className="flex flex-col">
          <h3 className="text-accent1 text-sm font-light italic mb-2">
            Select your level of service choice to see Knowtex's recommendation.
          </h3>
          <h3 className="text-accent1 text-sm font-semibold">Level:</h3>

          {/* FIRST row of level buttons */}
          <div
            className="flex flex-row gap-4"
            role="group"
            aria-label="Initial level of service selection"
          >
            {[2, 3, 4, 5].map((number) => (
              <button
                key={number}
                type="button"
                aria-label={`Initial level of service ${number}`}
                aria-pressed={activeButton === number}
                onClick={() => handleClick(number)}
                className={`border-accent1 border rounded-md hover:bg-transparent-purple 
              hover:text-accent1 text-accent1 font-bold py-2 px-4 w-12 h-12 
              flex items-center justify-center ${activeButton === number ? "bg-transparent-purple" : ""
                  } `}
              >
                {number}
              </button>
            ))}
          </div>

          {/* If user picks a different level, show second row */}
          {showRecommendations && emData && (
            <KnowtexEMSuggestion
              showRecommendations={showRecommendations}
              isRationaleProvided={isRationaleProvided}
              setIsRationaleProvided={setIsRationaleProvided}
              userRationale={userRationale}
              setUserRationale={setUserRationale}
              showRationale={showRationale}
              setShowRationale={setShowRationale}
              mdm_suggested={mdm_suggested}
              setFinalSelectedLevel={setFinalSelectedLevel}
              setFinalSelectedTs={setFinalSelectedTs}
              levels={levels}
              patient={patient}
              emData={emData}
              useTimeBasedCoding={useTimeBasedCoding}
              timeBasedLevel={timeBasedLevel}
              org={org}
            />
          )}
        </div>
      )}
    </div>
  );
}
